import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, User, Key, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleUpdateName = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/auth/profile`, { name }, { withCredentials: true });
      toast.success('Name updated successfully');
      checkAuth(); // Refresh user data
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update name';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/auth/profile`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true }
      );
      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update password';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <header className="border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="rounded-none text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl tracking-tight font-medium">Account Settings</h1>
              <p className="text-xs text-zinc-400 mt-0.5">Manage your account details</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Account Info */}
          <div className="border border-zinc-800 bg-zinc-900 p-6">
            <div className="flex items-center gap-3 mb-4">
              <User className="h-5 w-5 text-zinc-400" />
              <h2 className="text-xl font-medium">Account Information</h2>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-mono text-zinc-400">Email</p>
                <p className="text-sm mt-1 text-zinc-300">{user?.email}</p>
              </div>
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-mono text-zinc-400">Role</p>
                <span className="inline-block mt-1 px-3 py-1 text-xs bg-violet-900 text-violet-200">
                  {user?.role === 'admin' ? 'Administrator' : 'Service Person'}
                </span>
              </div>
            </div>
          </div>

          {/* Update Name */}
          <div className="border border-zinc-800 bg-zinc-900 p-6">
            <div className="flex items-center gap-3 mb-4">
              <User className="h-5 w-5 text-zinc-400" />
              <h2 className="text-xl font-medium">Update Name</h2>
            </div>
            <form onSubmit={handleUpdateName} className="space-y-4">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-mono text-zinc-400">
                  Name *
                </label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="rounded-none bg-violet-600 text-white hover:bg-violet-700"
              >
                <Save className="h-4 w-4 mr-2" />
                Update Name
              </Button>
            </form>
          </div>

          {/* Change Password */}
          <div className="border border-zinc-800 bg-zinc-900 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Key className="h-5 w-5 text-zinc-400" />
              <h2 className="text-xl font-medium">Change Password</h2>
            </div>
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-mono text-zinc-400">
                  Current Password *
                </label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white"
                  placeholder="Enter current password"
                  required
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-mono text-zinc-400">
                  New Password * (min 6 characters)
                </label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white"
                  placeholder="Enter new password"
                  required
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-mono text-zinc-400">
                  Confirm New Password *
                </label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white"
                  placeholder="Confirm new password"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="rounded-none bg-blue-600 text-white hover:bg-blue-700"
              >
                <Key className="h-4 w-4 mr-2" />
                Change Password
              </Button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
